import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axiosApexInstance from '../auth/axiosApexInstance';
import { useUser } from './UserContext';

// Action Subject interface
export interface ActionSubjectDTO {
  id: number;
  subject: string;
  category: string;
  isActive: number;
  sortOrder: number;
  description?: string;
}

// Action Type interface (extended from existing)
export interface ActionTypeDTO {
  id: number;
  name: string;
  categoryId: number;
  isActive: number;
  sortOrder: number;
  category?: string;
  description?: string;
}

// Action Status interface (extended from existing)
export interface ActionStatusDTO {
  id: number;
  status: string;
  description: string;
  isActive: number;
  sortOrder: number;
  color: string;
}

// Priority interface
export interface ActionPriorityDTO {
  id: number;
  priority: string;
  description: string;
  color: string;
  sortOrder: number;
}

// Facility interface
export interface FacilityDTO {
  id: number;
  name: string;
  isActive: number;
  sortOrder: number;
}

// Actions Context interface
interface ActionsContextType {
  // Data
  actionSubjects: ActionSubjectDTO[];
  actionTypes: ActionTypeDTO[];
  actionStatuses: ActionStatusDTO[];
  actionPriorities: ActionPriorityDTO[];
  facilities: FacilityDTO[];
  
  // Authentication state
  waitingForAuth: boolean;
  
  // Loading states
  loading: boolean;
  subjectsLoading: boolean;
  typesLoading: boolean;
  statusesLoading: boolean;
  prioritiesLoading: boolean;
  facilitiesLoading: boolean;
  
  // Error states
  error: string | null;
  subjectsError: string | null;
  typesError: string | null;
  statusesError: string | null;
  prioritiesError: string | null;
  facilitiesError: string | null;
  
  // Actions
  refreshAll: () => Promise<void>;
  refreshSubjects: () => Promise<void>;
  refreshTypes: () => Promise<void>;
  refreshStatuses: () => Promise<void>;
  refreshPriorities: () => Promise<void>;
  refreshFacilities: () => Promise<void>;
  
  // Utility methods
  getSubjectById: (id: number) => ActionSubjectDTO | undefined;
  getTypeById: (id: number) => ActionTypeDTO | undefined;
  getStatusById: (id: number) => ActionStatusDTO | undefined;
  getPriorityById: (id: number) => ActionPriorityDTO | undefined;
  getFacilityById: (id: number) => FacilityDTO | undefined;
  
  // Search and filter methods
  searchSubjects: (query: string) => ActionSubjectDTO[];
  getSubjectsByCategory: (category: string) => ActionSubjectDTO[];
  getTypesByCategory: (category: string) => ActionTypeDTO[];
}

// Default context value
const defaultActionsContext: ActionsContextType = {
  // Data
  actionSubjects: [],
  actionTypes: [],
  actionStatuses: [],
  actionPriorities: [],
  facilities: [],
  
  // Authentication state
  waitingForAuth: true,
  
  // Loading states
  loading: false,
  subjectsLoading: false,
  typesLoading: false,
  statusesLoading: false,
  prioritiesLoading: false,
  facilitiesLoading: false,
  
  // Error states
  error: null,
  subjectsError: null,
  typesError: null,
  statusesError: null,
  prioritiesError: null,
  facilitiesError: null,
  
  // Actions
  refreshAll: async () => {},
  refreshSubjects: async () => {},
  refreshTypes: async () => {},
  refreshStatuses: async () => {},
  refreshPriorities: async () => {},
  refreshFacilities: async () => {},
  
  // Utility methods
  getSubjectById: () => undefined,
  getTypeById: () => undefined,
  getStatusById: () => undefined,
  getPriorityById: () => undefined,
  getFacilityById: () => undefined,
  
  // Search and filter methods
  searchSubjects: () => [],
  getSubjectsByCategory: () => [],
  getTypesByCategory: () => [],
};

// Create the context
const ActionsContext = createContext<ActionsContextType>(defaultActionsContext);

// Provider component
interface ActionsProviderProps {
  children: ReactNode;
}

export const ActionsProvider: React.FC<ActionsProviderProps> = ({ children }) => {
  // Debug logging removed for production
  // console.log('ActionsProvider: Initializing...');
  
  // Get authentication state
  const { apexUser, user } = useUser();
  
  // State for all data
  const [actionSubjects, setActionSubjects] = useState<ActionSubjectDTO[]>([]);
  const [actionTypes, setActionTypes] = useState<ActionTypeDTO[]>([]);
  const [actionStatuses, setActionStatuses] = useState<ActionStatusDTO[]>([]);
  const [actionPriorities, setActionPriorities] = useState<ActionPriorityDTO[]>([]);
  const [facilities, setFacilities] = useState<FacilityDTO[]>([]);
  
  // State for authentication
  const [waitingForAuth, setWaitingForAuth] = useState(true);
  
  // Loading states
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [prioritiesLoading, setPrioritiesLoading] = useState(false);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  
  // Error states
  const [subjectsError, setSubjectsError] = useState<string | null>(null);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [statusesError, setStatusesError] = useState<string | null>(null);
  const [prioritiesError, setPrioritiesError] = useState<string | null>(null);
  const [facilitiesError, setFacilitiesError] = useState<string | null>(null);
  
  // Computed loading state
  const loading = subjectsLoading || typesLoading || statusesLoading || prioritiesLoading || facilitiesLoading;
  
  // Computed error state
  const error = subjectsError || typesError || statusesError || prioritiesError || facilitiesError;
  
  // Load action subjects
  const loadActionSubjects = useCallback(async () => {
    setSubjectsLoading(true);
    setSubjectsError(null);
    try {
      // Debug logging removed for production
      // console.log('Loading action subjects...');
      const response = await axiosApexInstance.get('/api/actions/subjects');
      // console.log('Action subjects response:', response.data);
      if (response.data && response.data.length > 0) {
        // console.log('Setting action subjects from API:', response.data);
        setActionSubjects(response.data);
      } else {
        // console.log('API returned empty data for action subjects');
        setActionSubjects([]);
      }
    } catch (err: any) {
      console.error('Error loading action subjects:', err);
      setSubjectsError(err.response?.data?.message || 'Failed to load action subjects');
      setActionSubjects([]);
    } finally {
      setSubjectsLoading(false);
    }
  }, []);
  
  // Load action types
  const loadActionTypes = useCallback(async () => {
    setTypesLoading(true);
    setTypesError(null);
    try {
      // Debug logging removed for production
      // console.log('Loading action types...');
      const response = await axiosApexInstance.get('/api/actions/types');
      // console.log('Action types response:', response.data);
      if (response.data && response.data.length > 0) {
        // console.log('Setting action types from API:', response.data);
        setActionTypes(response.data);
      } else {
        // console.log('API returned empty data for action types');
        setActionTypes([]);
      }
    } catch (err: any) {
      console.error('Error loading action types:', err);
      setTypesError(err.response?.data?.message || 'Failed to load action types');
      setActionTypes([]);
    } finally {
      setTypesLoading(false);
    }
  }, []);
  
  // Load action statuses
  const loadActionStatuses = useCallback(async () => {
    setStatusesLoading(true);
    setStatusesError(null);
    try {
      const response = await axiosApexInstance.get('/api/actions/statuses');
      setActionStatuses(response.data);
    } catch (err: any) {
      console.error('Error loading action statuses:', err);
      setStatusesError(err.response?.data?.message || 'Failed to load action statuses');
    } finally {
      setStatusesLoading(false);
    }
  }, []);
  
  // Load action priorities (hardcoded)
  const loadActionPriorities = useCallback(async () => {
    setPrioritiesLoading(true);
    setPrioritiesError(null);
    try {
      // Hardcoded priorities instead of backend call
      setActionPriorities([
        { id: 1, priority: 'Low', description: 'Low priority tasks', color: '#4caf50', sortOrder: 1 },
        { id: 2, priority: 'Normal', description: 'Normal priority tasks', color: '#2196f3', sortOrder: 2 },
        { id: 3, priority: 'High', description: 'High priority tasks', color: '#f44336', sortOrder: 3 }
      ]);
    } catch (err: any) {
      console.error('Error setting action priorities:', err);
      setPrioritiesError('Failed to set action priorities');
    } finally {
      setPrioritiesLoading(false);
    }
  }, []);
  
  // Load facilities
  const loadFacilities = useCallback(async () => {
    setFacilitiesLoading(true);
    setFacilitiesError(null);
    try {
      // Debug logging removed for production
      // console.log('Loading facilities...');
      const response = await axiosApexInstance.get('/api/actions/facilities');
      // Debug logging removed for production
      // console.log('Facilities response:', response.data);
      setFacilities(response.data);
    } catch (err: any) {
      console.error('Error loading facilities:', err);
      setFacilitiesError(err.response?.data?.message || 'Failed to load facilities');
      setFacilities([]);
    } finally {
      setFacilitiesLoading(false);
    }
  }, []);
  
  // Load all data
  const loadAllData = useCallback(async () => {
    await Promise.all([
      loadActionSubjects(),
      loadActionTypes(),
      loadActionStatuses(),
      loadActionPriorities(), // This is now just setting hardcoded data
      loadFacilities()
    ]);
  }, [loadActionSubjects, loadActionTypes, loadActionStatuses, loadActionPriorities, loadFacilities]);
  
  // Refresh methods
  const refreshAll = useCallback(async () => {
    await loadAllData();
  }, [loadAllData]);
  
  const refreshSubjects = useCallback(async () => {
    await loadActionSubjects();
  }, [loadActionSubjects]);
  
  const refreshTypes = useCallback(async () => {
    await loadActionTypes();
  }, [loadActionTypes]);
  
  const refreshStatuses = useCallback(async () => {
    await loadActionStatuses();
  }, [loadActionStatuses]);
  
  const refreshPriorities = useCallback(async () => {
    await loadActionPriorities();
  }, [loadActionPriorities]);
  
  const refreshFacilities = useCallback(async () => {
    await loadFacilities();
  }, [loadFacilities]);
  
  // Utility methods
  const getSubjectById = useCallback((id: number): ActionSubjectDTO | undefined => {
    return actionSubjects.find(subject => subject.id === id);
  }, [actionSubjects]);
  
  const getTypeById = useCallback((id: number): ActionTypeDTO | undefined => {
    return actionTypes.find(type => type.id === id);
  }, [actionTypes]);
  
  const getStatusById = useCallback((id: number): ActionStatusDTO | undefined => {
    return actionStatuses.find(status => status.id === id);
  }, [actionStatuses]);
  
  const getPriorityById = useCallback((id: number): ActionPriorityDTO | undefined => {
    return actionPriorities.find(priority => priority.id === id);
  }, [actionPriorities]);
  
  const getFacilityById = useCallback((id: number): FacilityDTO | undefined => {
    return facilities.find(facility => facility.id === id);
  }, [facilities]);
  
  // Search and filter methods
  const searchSubjects = useCallback((query: string): ActionSubjectDTO[] => {
    if (!query.trim()) return actionSubjects;
    const lowerQuery = query.toLowerCase();
    return actionSubjects.filter(subject => 
      subject.subject.toLowerCase().includes(lowerQuery) ||
      subject.description?.toLowerCase().includes(lowerQuery) ||
      subject.category.toLowerCase().includes(lowerQuery)
    );
  }, [actionSubjects]);
  
  const getSubjectsByCategory = useCallback((category: string): ActionSubjectDTO[] => {
    return actionSubjects.filter(subject => subject.category === category);
  }, [actionSubjects]);
  
  const getTypesByCategory = useCallback((category: string): ActionTypeDTO[] => {
    return actionTypes.filter(type => type.category === category);
  }, [actionTypes]);
  
  // Load data on mount, but only when authenticated
  useEffect(() => {
    // Debug logging removed for production
    // console.log('ActionsProvider: useEffect triggered, checking auth state');
    // console.log('ActionsProvider: user:', user?.uid, 'apexUser:', apexUser?.userId);
    
    if (user?.uid && apexUser?.userId) {
      // console.log('ActionsProvider: User authenticated, loading all data');
      setWaitingForAuth(false);
      loadAllData();
    } else {
      // console.log('ActionsProvider: User not authenticated yet, waiting...');
      setWaitingForAuth(true);
    }
  }, [user, apexUser, loadAllData]);

  // Debug logging for state changes (removed for production)
  // useEffect(() => {
  //   console.log('ActionsContext - actionTypes changed:', actionTypes);
  // }, [actionTypes]);

  // useEffect(() => {
  //   console.log('ActionsContext - actionSubjects changed:', actionSubjects);
  // }, [actionSubjects]);

  // useEffect(() => {
  //   console.log('ActionsContext - facilities changed:', facilities);
  // }, [facilities]);
  
  // Context value
  const contextValue: ActionsContextType = {
    // Data
    actionSubjects,
    actionTypes,
    actionStatuses,
    actionPriorities,
    facilities,
    
    // Authentication state
    waitingForAuth,
    
    // Loading states
    loading,
    subjectsLoading,
    typesLoading,
    statusesLoading,
    prioritiesLoading,
    facilitiesLoading,
    
    // Error states
    error,
    subjectsError,
    typesError,
    statusesError,
    prioritiesError,
    facilitiesError,
    
    // Actions
    refreshAll,
    refreshSubjects,
    refreshTypes,
    refreshStatuses,
    refreshPriorities,
    refreshFacilities,
    
    // Utility methods
    getSubjectById,
    getTypeById,
    getStatusById,
    getPriorityById,
    getFacilityById,
    
    // Search and filter methods
    searchSubjects,
    getSubjectsByCategory,
    getTypesByCategory,
  };
  
  // Debug logging removed for production
  // console.log('ActionsProvider: Context value created with:', {
  //   actionSubjects: actionSubjects.length,
  //   actionTypes: actionTypes.length,
  //   actionStatuses: actionStatuses.length,
  //   actionPriorities: actionPriorities.length,
  //   facilities: facilities.length,
  //   waitingForAuth: !(user?.uid && apexUser?.userId)
  // });
  
  return (
    <ActionsContext.Provider value={contextValue}>
      {children}
    </ActionsContext.Provider>
  );
};

// Custom hook to use the ActionsContext
export const useActions = (): ActionsContextType => {
  // Debug logging removed for production
  // console.log('useActions: Hook called');
  const context = useContext(ActionsContext);
  if (!context) {
    console.error('useActions: No context found - ActionsProvider not wrapping this component');
    throw new Error('useActions must be used within an ActionsProvider');
  }
  // console.log('useActions: Context found, returning:', {
  //   actionSubjects: context.actionSubjects.length,
  //   actionTypes: context.actionTypes.length,
  //   actionStatuses: context.actionStatuses.length,
  //   actionPriorities: context.actionPriorities.length,
  //   facilities: context.facilities.length
  // });
  return context;
};

export default ActionsContext; 